import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import { Typography } from '@mui/material';
import { Box } from '@mui/system';

import WaveUIDraggableOverlay from '@/shared/components/widgets/WaveUIDraggableOverlay';
import WaveUIStepper from '@/shared/components/widgets/WaveUIStepper';
import FulfilmentHistory from '@/shared/types/fulfilment/history.type';
import OrderProduct from '@/shared/types/order/orderProduct.type';

import HistoryItem from './HistoryItem';

type FulfilmentOrderHistoryProps = {
  history: FulfilmentHistory[];
  product: OrderProduct;
  initialPos?: { x: number; y: number };
  onClose: () => void;
  getDragging?: (dragging: boolean) => void;
};

const FulfilmentOrderHistory: FC<FulfilmentOrderHistoryProps> = ({
  history,
  product,
  initialPos,
  onClose,
  getDragging,
}) => {
  const { t } = useTranslation(['orders', 'common']);

  return (
    <WaveUIDraggableOverlay
      title={`${t('orders:fulfilmentDetails')}: ${product.product_name}`}
      initialPos={initialPos}
      onClose={onClose}
      getDragging={getDragging}
    >
      <Box
        sx={{
          p: 2,
          pt: 0,
          width: '488px',
        }}
      >
        <WaveUIStepper
          steps={[
            {
              label: 'Order Placed',
              icon: <TextSnippetOutlinedIcon />,
            },
            {
              label: 'Order Shipped',
              icon: <LocalShippingOutlinedIcon />,
            },
            {
              label: 'Order Delivered',
              icon: <HomeOutlinedIcon />,
            },
          ]}
          activeStep={1}
          sx={{ mb: 2 }}
        />

        <Typography variant='subtitle1'>
          {t('orders:fulfilmentHistory')}
        </Typography>

        <Box
          sx={{
            mt: 2,
            maxHeight: '40vh',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          {history.map((item) => (
            <HistoryItem key={item.history_id} historyItem={item} />
          ))}
        </Box>
      </Box>
    </WaveUIDraggableOverlay>
  );
};

export default FulfilmentOrderHistory;
